/**
 * Building — a stack of floors sharing one `Shell`, so cores/windows/footprint
 * align across stories (Phase 1d). Floors are ordered bottom → top:
 * `floors[0]` is the deepest basement, `floors[groundIdx]` the ground/entrance,
 * higher indices go up. Going UP = index+1, DOWN = index-1; the aligned stairs
 * tile is the same (x,y) on every floor.
 *
 * Basements are windowless (dark); above-ground floors get windows. Purpose runs
 * lobby (ground) → grind (middle) → important (top), with basements as service.
 *
 * Headless + deterministic: everything rolls from the establishment seed.
 */

import { buildProfile, type Establishment, type OperatingState } from '../establishment';
import { hashSeed, mulberry32 } from '../rng';
import { computeLightField } from '../light';
import type { MapData } from '../map';
import type { Rect } from './bsp';
import { generateFloorWithShell } from './floor';
import { pickShell, type Shell } from './shell';
import { buildServices, REQUIRED_SERVICES, type Service } from './services';
import type { RoomType } from './rooms';

export type FloorPurpose = 'basement' | 'lobby' | 'grind' | 'important';

export interface Floor {
  map: MapData;
  cells: Rect[];
  /** The aligned stair PAIR (same (x,y) on every floor). */
  stairs: { up: { x: number; y: number }; down: { x: number; y: number } };
  /** Stack index (0 = deepest basement, increasing upward). */
  index: number;
  /** Signed floor number for display: ground = 1, up = 2.., basements = -1.. */
  floorNum: number;
  purpose: FloorPurpose;
  isBasement: boolean;
}

export interface Building {
  establishment: Establishment;
  shell: Shell;
  floors: Floor[];
  /** The floor the player is currently on (starts on the ground/entrance floor). */
  activeFloorIdx: number;
  /** Tamperable building services — one per required system (see services.ts). */
  services: Service[];
  /** Comms sabotaged → guards can't call for backup (broadcast suppressed). */
  commsDown: boolean;
}

function randInt(rng: () => number, range: readonly [number, number]): number {
  const [lo, hi] = range;
  return lo + Math.floor(rng() * (hi - lo + 1));
}

/**
 * Which machine rooms each floor must contain. Real buildings bury their plant:
 * every required service's room is dealt round-robin across the BASEMENTS
 * (deepest first), so a sabotage run means going down. Two exceptions: the
 * comms MDF rides with the server room at the TOP of the stack, and a building
 * with no basement at all puts its plant on the ground floor.
 *
 * A floor seats as many as its cell count allows (`classifyRooms`); anything
 * that doesn't fit falls back to a Utility room in `buildServices`.
 */
function planPlantRooms(total: number, groundIdx: number): RoomType[][] {
  const perFloor: RoomType[][] = Array.from({ length: total }, () => []);
  const buried = REQUIRED_SERVICES
    .filter((r) => r.kind !== 'comms' && r.kind !== 'power') // power = the Utility room every floor already has
    .map((r) => r.room as RoomType);
  const hosts = groundIdx > 0 ? Array.from({ length: groundIdx }, (_v, i) => i) : [groundIdx];
  buried.forEach((room, i) => perFloor[hosts[i % hosts.length]!]!.push(room));
  perFloor[total - 1]!.push('server'); // comms MDF, top of the stack
  return perFloor;
}

/**
 * The room a mission type needs to exist for its objective to have an anchor.
 * Generation guarantees plenty already (safes, the comms MDF, security), but a
 * private office to find the mark in, or an archive to photograph, is otherwise
 * left to the dice — and a mission whose subject doesn't exist reads as broken
 * even when the questline falls back gracefully.
 */
const MISSION_ROOM: Record<string, RoomType> = {
  assassination: 'private-office',
  heist: 'records',
  'data-theft': 'server',
  recon: 'records',
  extraction: 'private-office',
  combined: 'private-office',
};

/** Generate a full multi-floor building for an establishment. */
export function generateBuilding(
  establishment: Establishment,
  opts: { operating?: OperatingState; missionType?: string } = {},
): Building {
  const profile = buildProfile(establishment.budget);
  const operating = opts.operating ?? 'in-use';
  const srng = mulberry32(hashSeed(establishment.seed, 0xb01));
  const shell = pickShell(profile, srng);

  const above = randInt(srng, profile.floors); // includes the ground floor
  const basements = randInt(srng, profile.basements);
  const groundIdx = basements;
  const total = above + basements;

  const plantRooms = planPlantRooms(total, groundIdx);
  // The mission's subject goes on the top floor — the one the plan calls
  // "important", and the one a questline anchors to first.
  // FIRST in the top floor's list: rooms are seated in order until the floor
  // runs out of cells, and the mission's subject outranks the comms MDF — a
  // contract whose subject doesn't exist is worse than one you have to hunt for.
  const missionRoom = opts.missionType ? MISSION_ROOM[opts.missionType] : undefined;
  if (missionRoom) plantRooms[total - 1]!.unshift(missionRoom);
  const floors: Floor[] = [];
  for (let i = 0; i < total; i++) {
    const isBasement = i < groundIdx;
    const isEntrance = i === groundIdx;
    const floorNum = isBasement ? i - groundIdx : i - groundIdx + 1; // ground=1, B1=-1
    const purpose: FloorPurpose = isBasement
      ? 'basement'
      : isEntrance
        ? 'lobby'
        : i === total - 1
          ? 'important'
          : 'grind';
    const fseed = hashSeed(establishment.seed, i + 1, 0xf10);
    const { map, cells, stairs } = generateFloorWithShell(shell, profile, fseed, {
      title: establishment.name,
      floorIndex: floorNum,
      operating,
      isBasement,
      isEntrance,
      plantRooms: plantRooms[i]!,
    });
    floors.push({ map, cells, stairs, index: i, floorNum, purpose, isBasement });
  }

  const building: Building = { establishment, shell, floors, activeFloorIdx: groundIdx, services: [], commsDown: false };
  // Place service plants, then re-bake light on any floor a (solid) plant landed
  // on so it occludes correctly.
  building.services = buildServices(building);
  for (const fi of new Set(building.services.map((s) => s.floorIdx))) {
    computeLightField(building.floors[fi]!.map);
  }
  return building;
}
